import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { RxChevronDown } from 'react-icons/rx';

import styled, { css } from 'styled-components';

import { scrollBar } from 'styles/commons/scrollBar';

/**
 * 드롭다운 컴포넌트
 * @param {string} className - 드롭다운의 추가 클래스 이름
 * @param {string} title - 드롭다운의 제목
 * @param {string} label - 드롭다운에 표시될 레이블
 * @param {string} type - 드롭다운의 입력 타입
 * @param {string} name - 드롭다운의 입력 이름
 * @param {Array} list - 드롭다운 메뉴 항목 목록
 * @param {function} onDropDownClick - 드롭다운 항목 클릭 핸들러
 * @param {boolean} disabled - 드롭다운 비활성화 상태
 * @param {boolean} isFullWidth - 드롭다운 너비를 부모 컨테이너에 맞게 100%로 설정할지 여부
 */
const DropDown = ({
  className,
  title,
  label,
  type,
  name,
  list,
  onDropDownClick,
  disabled,
  isFullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectboxRef = useRef(null);
  const tempRef = useRef(null); // 문자열 길이 측정을 위한 임시 ref
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * 외부 클릭 시 드롭다운을 닫기 위한 핸들러
   * @param {Event} event - 클릭 이벤트
   */
  const handleClickOutside = (event) => {
    if (selectboxRef.current && !selectboxRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  /**
   * 드롭다운의 너비를 가장 긴 항목에 맞게 조정하는 효과
   */
  useLayoutEffect(() => {
    if (isFullWidth) {
      setDropdownWidth('100%');
      setIsInitialized(true);
      return;
    }

    if (tempRef.current) {
      const longestWidth = tempRef.current.offsetWidth + 40;

      setDropdownWidth(`${longestWidth}px`);
      setIsInitialized(true);
    }
  }, [list, isFullWidth]);

  return (
    <DropDownBox
      width={dropdownWidth}
      $isInitialized={isInitialized}
      $isFullWidth={isFullWidth}
      className={`dropdown ${isOpen ? 'open' : ''} ${className || ''}`}
      onClick={() => !disabled && setIsOpen((prev) => !prev)}
      ref={selectboxRef}
    >
      <DropDownLabel
        width={dropdownWidth}
        className={[
          'dropdown_label',
          isOpen ? 'open' : '',
          disabled ? 'disabled' : '',
        ]
          .join(' ')
          .trim()}
      >
        {label}
        <ArrowIcon $isOpen={isOpen.toString()}>
          <RxChevronDown />
        </ArrowIcon>
      </DropDownLabel>
      {type && (
        <input
          type={type}
          name={name}
          value={label}
          style={{ display: 'none' }}
          readOnly
        />
      )}
      {isOpen && (
        <DropDownMenu
          className={`${isOpen ? 'open' : ''} select_option dropdown_menu`}
          top={(title === undefined).toString()}
        >
          {list?.map((item, idx) => (
            <DropdownItem
              key={idx.toString()}
              className={isOpen ? 'open' : ''}
              data-type={item.dataType}
              onClick={() => onDropDownClick(item)}
            >
              {item.value}
            </DropdownItem>
          ))}
        </DropDownMenu>
      )}
      <HiddenText ref={tempRef}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: 'fit-content',
          }}
        >
          {(list ?? []).map((item, i) => (
            <div key={i}>{item.value}</div>
          ))}
        </div>
      </HiddenText>
    </DropDownBox>
  );
};

export default memo(DropDown);

const DropDownBox = styled.div`
  position: relative;
  display: ${({ $isFullWidth }) => ($isFullWidth ? 'block' : 'inline-block')};
  width: ${({ width }) => width};
  height: 100%;
  /* margin-left: 5px; */
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  opacity: ${({ $isInitialized }) => ($isInitialized ? 1 : 0)};
  pointer-events: ${({ $isInitialized }) => ($isInitialized ? 'auto' : 'none')};
  transition:
    opacity 0.3s ease,
    border-color 0.3s ease,
    background-color 0.3s ease,
    box-shadow 0.3s ease;
`;
// 드롭다운 레이블 스타일
const DropDownLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.gray200};
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    height: 36px;
  }

  &.disabled {
    background: #f7f9fa;
    color: ${({ theme }) => theme.color.gray500};
  }
`;

const ArrowIcon = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  vertical-align: middle;
  svg {
    width: 15px;
    height: 15px;
    fill: ${({ theme }) => theme.color.gray700};
  }
  ${({ $isOpen }) =>
    $isOpen === 'true'
      ? css`
          svg {
            transform: rotateZ(180deg);
          }
        `
      : css`
          svg {
            transform: rotateZ(0deg);
          }
        `}
`;
// 드롭다운 메뉴 스타일
export const DropDownMenu = styled.ul`
  position: absolute;
  left: 0;
  width: 100%;
  padding: 4px 0;
  margin-top: -4px;
  z-index: 10;
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.gray200};
  opacity: 0;
  border-radius: 0 0 6px 6px;

  top: ${({ top }) => (top === 'true' ? '40px' : '65px')};
  max-height: 300px;
  ${scrollBar.default}

  &.open {
    opacity: 1;
  }

  // top: 35px;
`;
// 드롭다운 항목 스타일
const DropdownItem = styled.li`
  font-size: 14px;
  padding: 8px 12px;
  text-align: left;
  color: ${({ theme }) => theme.color.gray900};
  white-space: nowrap;

  &:before {
    display: none;
  }

  &:hover {
    background: ${({ theme }) => theme.color.gray80};
  }

  a {
    color: ${({ theme }) => theme.color.gray900};
  }
`;

// 숨겨진 텍스트를 사용하여 가장 긴 아이템의 너비 측정
const HiddenText = styled.span`
  position: absolute;
  display: inline-block;
  visibility: hidden;
  white-space: nowrap;
  font-size: 14px;
  padding: 0 12px;
  pointer-events: none;
`;
